export enum GeocodeStatus {
    /** Job running — progress frames arriving. Never actually sent on the wire (see GeocodeJobEvent). */
    IN_PROGRESS = "GEOCODING_IN_PROGRESS",
    /**
     * The job itself finished. Per-location outcomes would be in
     * `GeocodeSummary.failed` — an empty array is the "all resolved" screen,
     * a non-empty one is "needs attention". Both are this same status; they
     * are not separate states. Confirmed wire value: "GEOCODING_COMPLETE".
     */
    COMPLETE = "GEOCODING_COMPLETE",
    /** The geocoding process itself errored (exception/crash) — not the same as a location needing attention. */
    FAILED = "GEOCODING_FAILED",
}

/** One location that could not be geocoded automatically. */
export interface GeocodeFailedLocation {
    rowNumber: number;
    address: string;
}

/**
 * One row from `GET /sov/{jobId}/geocode/not-found` — the locations that need
 * manual attention after a geocode job completes.
 *
 * `rawData` is the source row exactly as it was parsed: an open-ended map, so
 * its keys vary per workbook (and can vary per sheet within one workbook).
 * That is what the attention table's dynamic columns are built from.
 */
export interface GeocodeNotFoundLocation {
    /** Server identity for the row. Echoed back unchanged on update. */
    id?: string;
    rowIndex: number;
    sheetOfOrigin: string;
    rawData: { [key: string]: unknown };
    /**
     * Coordinates sit BESIDE `rawData`, not inside it — `rawData` only holds
     * the sheet's own columns. The Coordinates column is therefore appended to
     * the dynamic set rather than discovered from `rawData` keys.
     */
    lat?: number | string | null;
    lng?: number | string | null;
    /** The editable pair. `lat`/`lng` are informational only. */
    latLng?: string | null;
}

/** Outcome of reading the Coordinates cell. */
export interface ParsedCoordinates {
    /** Present only when the cell held a valid pair. */
    coordinates: { lat: number; lng: number } | null;
    /** Present when the cell held something that isn't a valid pair. */
    error: string | null;
}

/** A dynamic column of the attention table, derived from `rawData` keys. */
export interface AttentionColumn {
    /** The `rawData` key this column reads. */
    key: string;
    /** Display label — the key humanised, since the API sends no labels. */
    label: string;
}

/** An editable row of the attention table. */
export interface AttentionRow {
    /**
     * Stable identity. `rowIndex` alone is NOT unique — two sheets in the same
     * workbook both have a row 1 — so this pairs it with the sheet.
     */
    id: string;
    rowIndex: number;
    sheetOfOrigin: string;
    /** Current value per column key; edited in place by the inputs. */
    values: { [key: string]: string };
    /** Which keys arrived with a value, so we can tell "blank" from "user cleared it". */
    readonly originalValues: { [key: string]: string };
    /** `lat, lng` from the response — informational, shown as a hint on the Coordinates input. */
    coordinateHint: string | null;
    /**
     * The row exactly as the API sent it. Kept so an update payload can be
     * rebuilt from it — the server expects the whole object back (id,
     * sheetOfOrigin, lat/lng and any rawData keys that have no column), not
     * just the fields the table happens to render.
     */
    source: GeocodeNotFoundLocation;
    /** True while this row's own update request is in flight. */
    isSaving: boolean;
    /** Set when this row's update failed. */
    error: string | null;
}

/**
 * Terminal result of a geocode job that completed (GeocodeStatus.COMPLETE).
 *
 * `rooftopCount`/`approximationCount`/`failedCount` are real, accumulated
 * from every progress frame's per-batch breakdown (e.g. "40 geocoded: 1
 * rooftop, 17 approximation, 22 not found") — rooftop + approximation both
 * count as successfully geocoded, "not found" is the real failure count.
 * `failedCount > 0` is what actually drives the "needs attention" screen.
 *
 * `failed` (the itemized per-row list, with addresses) is separate and
 * still always empty — no frame seen so far carries individual row/address
 * detail, only the aggregate counts above. So "needs attention" can show
 * the right numbers today, but not yet a fixable row-by-row table.
 */
export interface GeocodeSummary {
    totalLocations: number;
    /** Derived, not confirmed: totalLocations minus everything geocoded/failed. */
    preserved: number;
    /** rooftopCount + approximationCount. */
    geocoded: number;
    rooftopCount: number;
    approximationCount: number;
    /** Real, accumulated "not found" count — this is what allResolved/needs-attention is based on. */
    failedCount: number;
    /** Always empty today — see class doc. */
    failed: GeocodeFailedLocation[];
}

/**
 * Emitted over the course of a geocode job — see MaiaGeocodeService.
 *
 * `percent` on the IN_PROGRESS variant is client-computed, not sent by the
 * server: the first frame carries `totalRows` as the denominator, and each
 * later frame's message ("Processed batch of 500 row(s)...") supplies the
 * numerator via the batch size it reports. Capped at 99 until the terminal
 * frame actually arrives, so rounding/estimation error can't show 100%
 * before the job has really finished.
 */
export type GeocodeEvent =
    | { status: GeocodeStatus.IN_PROGRESS; message: string; percent: number }
    // No summary: the stream carries no trustworthy counts. Preserved /
    // Geocoded / Failed are read from GET /sov/upload/{jobId}/summary.
    | { status: GeocodeStatus.COMPLETE }
    | { status: GeocodeStatus.FAILED; error: string };

/**
 * Response of `POST /sov/{jobId}/geocode` — confirmed against the real
 * endpoint. This is just a "job started" acknowledgment, not a terminal
 * state — always follow it with a fresh `GET /sov/{jobId}/events`
 * connection (a new one per stage; the connection used to watch upload's
 * parsing has already closed and cannot be reused).
 */
export interface GeocodeApiResponse {
    jobId: number;
    /** Only confirmed value so far is "STARTED". */
    stageStatus: string;
    message?: string;
}

/**
 * A frame from `GET /sov/{jobId}/events` while watching a geocode job.
 * Confirmed shape — reuses the endpoint/connection handling built for
 * upload, but the payload itself differs:
 *
 * - Progress frames (`event: "progress"`): usually just `jobId` and
 *   `message` — no `status`, no server-computed percent. The batch size is
 *   embedded in the free-text message, e.g. "Processed batch of 500 row(s)
 *   (450 geocoded: 400 rooftop, 30 approximation, 20 not found)". The very
 *   first frame is the exception: it carries `status: "GEOCODING_IN_PROGRESS"`
 *   and `totalRows` (the job's total row count), with no `message` — that's
 *   the denominator MaiaGeocodeService uses to compute a real percentage
 *   from the batch sizes reported afterward.
 * - Terminal frames (`event: "done"` / `"error"`): only `jobId` and
 *   `status` are guaranteed; error frames additionally guarantee `error`.
 *   `totalRows`/`sheets` are optional extras that only appear when the
 *   connection opens *after* the job already finished (a snapshot on
 *   connect) — never assume they're present.
 */
export interface GeocodeJobEvent {
    jobId?: number;
    status?: GeocodeStatus;
    message?: string;
    totalRows?: number;
    error?: string;
}
